import { makeStyles } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { useData } from '../../../data'
import { trackPromise, usePromiseTracker } from 'react-promise-tracker'
import { Button, Table, Tooltip, Space, Popover } from 'antd'
import { EyeOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import moment from 'moment'

const useStyles = makeStyles(theme => ({
    table: {
        width: '100%',
        height: '100%'
    },
    nameCell: {
        minWidth: '25%',
        marginRight: theme.spacing(2)
    },
}))

const ToolsTableView = ({
    tools, onSelectTool, loading, count, limit, page, updateLimit,
    updatePage, setOrdering, editTool
}) => {
    const classes = useStyles()
    const data = useData()
    const auth = data.auth()
    const [popupState, setPopupState] = useState({})
    const [isSuperUser, setIsSuperUser] = useState(false);

    /**
     * Convert the given text to title case
     *
     * @param {string} text - string to convert to title
    */
    const titleCase = text => {
        if (text) {
            return text[0].toUpperCase() + text.slice(1)
        }
        return text
    }

    const handleEditClick = record => {
        handlePopupVisibleChange(record.id)
        editTool(record)
    }

    const handlePopupVisibleChange = id => {
        setPopupState(x => ({ ...x, [id]: !x[id] }))
    }

    // load the current user
    const loadUser = () => {
        trackPromise(
            auth.getCurrentUser()
                .then((val) => setIsSuperUser(val.is_superuser))
                .catch(console.log), 'get_user'
        )
    }

    // get the currently signed in user
    useEffect(() => {
        loadUser()
    }, [isSuperUser])

    /** Function that displays Edit & View actions Popup */
    const actions = record => {
        return (
            <Space direction="vertical">
                <Button type='text' onClick={() => onSelectTool(record)} size="small">
                    <EyeOutlined /> View
                </Button>
                {isSuperUser ? (
                    <Button type='text' onClick={() => handleEditClick(record)} size="small">
                        <EditOutlined /> Edit
                    </Button>
                ) : ([])}
            </Space>
        )
    }

    /**
     * Tools listing table columns
     */
    const toolsColumns = [
        {
            key: 'id',
            title: 'Id',
            dataIndex: 'id',
            sorter: (a, b) => a - b,
            render: (text, record) => <Button type="link" onClick={() => onSelectTool(record)} href={"/dashboard/tool?id=" + record.id} size="small">{text}</Button>
        },
        {
            key: 'name',
            title: 'Name',
            dataIndex: 'name',
            sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            render: (text, record) => <Button type="link" onClick={() => onSelectTool(record)} href={"/dashboard/tool?id=" + record.id} size="small">
                <span className={classes.nameCell}>{text}</span> </Button>
        },
        {
            key: 'type',
            title: 'Type',
            dataIndex: 'type',
            sorter: (a, b) => a.type.toLowerCase().localeCompare(b.type.toLowerCase())
        },
        {
            key: 'cookie_key',
            title: 'Cookie Key',
            dataIndex: 'cookie_key',
            sorter: (a, b) => a.type.toLowerCase().localeCompare(b.type.toLowerCase())
        },
        {
            key: 'cat',
            title: 'Category',
            dataIndex: 'category',
            sorter: (a, b) => a.category.toLowerCase().localeCompare(b.category.toLowerCase()),
            render: (text, _) => (
                <span>{titleCase(text)}</span>
            )
        },
        {
            key: 'spider_name',
            title: 'Spider name',
            dataIndex: 'spider_name',
            render: (text, _) => (
                <span>{text}</span>
            )
        },
        {
            key: 'created_at',
            title: 'Created at',
            dataIndex: 'created_at',
            render: (text, _) => <Tooltip title={moment(text).format('YYYY-MM-DD HH:mm')}>
                {moment(text).format('YYYY-MM-DD')}
            </Tooltip>
        },
        {
            key: 'action',
            title: 'Actions',
            render: (_, record) => (
                <Popover
                    placement="left"
                    visible={popupState[record.id]}
                    content={() => actions(record)}
                    trigger="click"
                    onVisibleChange={() => handlePopupVisibleChange(record.id)}
                ><MoreOutlined /></Popover>
            )
        }
    ]

    const changePage = (page, _) => {
        updatePage(page);
    }

    const changeSize = (page, size) => {
        updateLimit(size)
    }

    const handleTableChange = (pagination, filters, sorter) => {
        const field = sorter.field;
        const order = sorter.order;

        if (field && order) {
            const ordering = order == "descend" ? `-${field}` : field;
            setOrdering(ordering);
        }
    }

    return (
        <Table
            columns={toolsColumns}
            dataSource={tools}
            className={classes.table}
            loading={loading}
            onChange={handleTableChange}
            pagination={{
                onChange: changePage,
                onShowSizeChange: changeSize,
                showSizeChanger: true,
                total: count,
                current: page,
                pageSize: limit,
            }}
        />
    )
}

ToolsTableView.propTypes = {
    /** List of registered tools */
    tools: PropTypes.array,
    /** Show tool details */
    onSelectTool: PropTypes.func,
    /** Show edit tool drawer */
    editTool: PropTypes.func,
    /** add a new job for this tool */
    addJob: PropTypes.func,
    /** Is table data loading */
    loading: PropTypes.bool
}

export default ToolsTableView
